import apiClient from './axios';
import type { ApiEnvelope } from '../types';

export interface UploadedImage {
  url: string;
  publicId: string;
}

export const uploadsApi = {
  /** Uploads a base64-encoded image (data URI or raw base64) and
   *  returns the hosted Cloudinary URL. Backend validates format
   *  (JPG/PNG/WebP) and size (5MB). */
  async productImage(imageBase64: string): Promise<UploadedImage> {
    const res = await apiClient.post<ApiEnvelope<UploadedImage>>(
      '/vendor/uploads/product-image',
      { imageBase64 },
    );
    return res.data.data;
  },
};

/** Read a File as a base64 data URI. Resolves with the
 *  `data:image/...;base64,...` string that the backend accepts. */
export function fileToBase64DataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file.'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

/** Client-side guard so the user gets immediate feedback on obviously
 *  invalid files. Backend still validates authoritatively. */
export function validateImageFile(file: File): string | null {
  const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_BYTES = 5 * 1024 * 1024;
  if (!ALLOWED.includes(file.type)) {
    return 'Only JPG, PNG, or WebP images are allowed.';
  }
  if (file.size > MAX_BYTES) {
    return 'Image must be under 5MB.';
  }
  return null;
}
